import { Plus, Trash2 } from 'lucide-react';
import type { UnitOption } from '@/api/cockpit';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { manualLineDraftErrors, type ManualLineDraft } from '../helpers/manual-line-drafts';
import type { ProductPickerState } from './LineItemsTable';
import { ProductSelector } from './ProductSelector';

export function ManualLinesEditor(props: {
  lines: readonly ManualLineDraft[];
  unitOptions: readonly UnitOption[];
  disabled: boolean;
  showErrors: boolean;
  saveError: string | null;
  productPicker: Omit<ProductPickerState, 'selectedProducts' | 'onProductSelect'>;
  onAdd: () => void;
  onChange: (id: string, patch: Partial<ManualLineDraft>) => void;
  onRemove: (id: string) => void;
}): JSX.Element | null {
  if (props.disabled && props.lines.length === 0) return null;
  return (
    <div className="mt-4 space-y-3">
      <Button type="button" variant="outline" disabled={props.disabled} onClick={props.onAdd}>
        <Plus className="mr-2 h-4 w-4" /> Add Line
      </Button>
      {props.saveError && (
        <p role="alert" className="text-sm text-destructive">
          {props.saveError}
        </p>
      )}
      {props.lines.map((line, index) => {
        const key = -index - 1;
        const errors = props.showErrors ? manualLineDraftErrors(line) : {};
        const id = (field: string) => `manual-${line.id}-${field}`;
        const error = (field: string) =>
          errors[field] ? (
            <p id={`${id(field)}-error`} className="text-xs text-destructive">
              {errors[field]}
            </p>
          ) : null;
        return (
          <section
            key={line.id}
            aria-label={`New line ${index + 1}`}
            className="space-y-3 rounded-md border bg-muted/10 p-4"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold">New line {index + 1}</h3>
                <p className="text-xs text-muted-foreground">Included when you save corrections.</p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={`Remove new line ${index + 1}`}
                disabled={props.disabled}
                onClick={() => props.onRemove(line.id)}
              >
                <Trash2 className="mr-2 h-4 w-4" /> Remove
              </Button>
            </div>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
              <div className="space-y-1 xl:col-span-2">
                <Label>Product *</Label>
                <ProductSelector
                  line={{ lineNumber: key, matchedProductDisplay: line.productDisplay }}
                  disabled={props.disabled}
                  isOpen={props.productPicker.productPopoverLine === key}
                  searchValue={props.productPicker.searchValue}
                  debouncedSearchValue={props.productPicker.debouncedSearchValue}
                  searchResults={props.productPicker.searchResults}
                  selectedProducts={{ [key]: line.productCode }}
                  onOpenChange={(open) => {
                    props.productPicker.onProductPopoverLineChange(open ? key : null);
                    props.productPicker.onSearchChange('');
                  }}
                  onSearchChange={props.productPicker.onSearchChange}
                  onSelect={(productCode) => {
                    const product = props.productPicker.searchResults.find(
                      (option) => option.itemNumber === productCode,
                    );
                    props.onChange(line.id, {
                      productCode,
                      productDisplay: product?.display ?? productCode,
                    });
                    props.productPicker.onProductPopoverLineChange(null);
                  }}
                />
                {error('productCode')}
              </div>
              <div className="space-y-1">
                <Label htmlFor={id('quantity')}>Quantity *</Label>
                <Input
                  id={id('quantity')}
                  value={line.quantity}
                  inputMode="decimal"
                  disabled={props.disabled}
                  aria-invalid={Boolean(errors.quantity)}
                  aria-describedby={errors.quantity ? `${id('quantity')}-error` : undefined}
                  onChange={(event) => props.onChange(line.id, { quantity: event.target.value })}
                />
                {error('quantity')}
              </div>
              <div className="space-y-1">
                <Label htmlFor={id('unit')}>Unit *</Label>
                <Select
                  value={line.unitOfMeasure}
                  disabled={props.disabled}
                  onValueChange={(unitOfMeasure) => props.onChange(line.id, { unitOfMeasure })}
                >
                  <SelectTrigger id={id('unit')} aria-invalid={Boolean(errors.unitOfMeasure)}>
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent>
                    {props.unitOptions.map((unit) => (
                      <SelectItem key={unit.symbol} value={unit.symbol}>
                        {unit.display}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {error('unitOfMeasure')}
              </div>
              <div className="space-y-1">
                <Label htmlFor={id('unitPrice')}>PO unit price</Label>
                <Input
                  id={id('unitPrice')}
                  value={line.unitPrice}
                  inputMode="decimal"
                  disabled={props.disabled}
                  aria-invalid={Boolean(errors.unitPrice)}
                  onChange={(event) => props.onChange(line.id, { unitPrice: event.target.value })}
                />
                {error('unitPrice')}
              </div>
              <div className="space-y-1">
                <Label htmlFor={id('currency')}>PO currency</Label>
                <Input
                  id={id('currency')}
                  value={line.currency}
                  placeholder="e.g. USD"
                  maxLength={3}
                  disabled={props.disabled}
                  aria-invalid={Boolean(errors.currency)}
                  onChange={(event) =>
                    props.onChange(line.id, { currency: event.target.value.toUpperCase() })
                  }
                />
                {error('currency')}
              </div>
              <div className="space-y-1 md:col-span-2 xl:col-span-4">
                <Label htmlFor={id('reason')}>Reason for adding the line *</Label>
                <Textarea
                  id={id('reason')}
                  value={line.reason}
                  maxLength={500}
                  disabled={props.disabled}
                  aria-invalid={Boolean(errors.reason)}
                  onChange={(event) => props.onChange(line.id, { reason: event.target.value })}
                />
                {error('reason')}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              D365 pricing and order validations apply to this line.
            </p>
          </section>
        );
      })}
    </div>
  );
}
